import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { MoniebookLoader } from '@moniebook-web/components/molecules/moniebook-loader';
import { ProtectedView } from '@moniebook-web/components/molecules/protected-view';
import { Outlet, type RouteObject } from 'react-router';

import { StyleScope } from '@/features/shared/style-scope';

const ENABLE_PRICE_REVIEW_AND_BILL_FLAG =
    'moniebook.pos.enablePriceReviewAndBill';

const ENABLE_PROCUREMENT_SETTINGS = 'moniebook.pos.enableProcurementSettings';

const ENABLE_ITEM_MODIFIERS = 'moniebook.pos.enableItemModifiers';

const ITEMS_PERMISSIONS = [
    'MANAGE_ITEM',
    'VIEW_ITEM',
    'CREATE_ITEM',
    'EDIT_ITEM',
    'DELETE_ITEM',
    'IMPORT_ITEM',
    'EXPORT_ITEM',
];
const MANAGE_MEASUREMENT_UNITS_PERMISSIONS = [
    'CREATE_MEASUREMENT_UNIT',
    'VIEW_MEASUREMENT_UNIT',
    'EDIT_MEASUREMENT_UNIT',
    'DELETE_MEASUREMENT_UNIT',
    'MANAGE_MEASUREMENT_UNIT',
];
const PRICING_GROUPS_PERMISSIONS = [
    'VIEW_PRICING_GROUP',
    'CREATE_PRICING_GROUP',
    'UPDATE_PRICING_GROUP',
    'DELETE_PRICING_GROUP',
];
const STOCK_HISTORY_PERMISSIONS = ['VIEW_STOCK_HISTORY'];
const STOCK_ADJUSTMENT_PERMISSIONS = [
    'VIEW_STOCK_ADJUSTMENT',
    'MANAGE_STOCK_ADJUSTMENT',
];
const SUPPLIERS_PERMISSIONS = [
    'VIEW_SUPPLIER',
    'EDIT_SUPPLIER',
    'DELETE_SUPPLIER',
    'MANAGE_SUPPLIER',
];
const REPORTS_PERMISSIONS = ['VIEW_REPORT'];
const STOCK_TRANSFER_PERMISSIONS = [
    'CANCEL_STOCK_TRANSFER',
    'VIEW_STOCK_TRANSFER',
    'MANAGE_STOCK_TRANSFER',
    'CREATE_STOCK_TRANSFER',
    'RECEIVE_STOCK_TRANSFER',
];
const PRODUCTION_AND_RECIPES_PERMISSIONS = [
    'VIEW_PRODUCTIONS',
    'CREATE_PRODUCTIONS',
    'CAN_REVERT_PRODUCTIONS',
    'VIEW_RECIPES',
    'CREATE_RECIPES',
    'DELETE_RECIPE',
];
const PURCHASE_ORDERS_PERMISSIONS = [
    'VIEW_PURCHASE_ORDERS',
    'CREATE_PURCHASE_ORDERS',
    'APPROVE_PURCHASE_ORDERS',
    'DELETE_PURCHASE_ORDERS',
    'DRAFT_PURCHASE_ORDERS',
];
const STOCK_RECONCILIATION_PERMISSIONS = [
    'VIEW_STOCK_RECONCILIATION',
    'CREATE_STOCK_RECONCILIATION',
    'APPROVE_STOCK_RECONCILIATION',
];

const GRN_PERMISSIONS = ['VIEW_GRN', 'CREATE_GRN', 'APPROVE_GRN'];

const BILL_PERMISSIONS = [
    'VIEW_BILL',
    'MANAGE_BILL',
    'CREATE_BILL',
    'APPROVE_BILL',
];

const BATCH_MANAGEMENT_PERMISSIONS = [
    ...STOCK_ADJUSTMENT_PERMISSIONS,
    ...ITEMS_PERMISSIONS,
];

export const productsRoutes: RouteObject[] = [
    {
        path: '',
        element: <StyleScope name='products-ui' />,
        children: [
            {
                path: 'items',
                element: (
                    <ProtectedView
                        accessCodeList={ITEMS_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { itemList } = await unwrapFeatures();
                            return itemList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView accessCodeList={['CREATE_ITEM']} />
                        ),
                        children: [
                            {
                                path: 'item-type-selection',
                                async lazy() {
                                    const { itemTypeSelection } =
                                        await unwrapFeatures();
                                    return itemTypeSelection().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: 'create',
                                async lazy() {
                                    const { createItem } =
                                        await unwrapFeatures();
                                    return createItem().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView accessCodeList={['EDIT_ITEM']} />
                        ),
                        children: [
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { updateItem } =
                                        await unwrapFeatures();
                                    return updateItem().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: 'print-labels',
                        async lazy() {
                            const { printLabels } = await unwrapFeatures();
                            return printLabels().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'modifiers',
                element: (
                    <FeatureProtectedView
                        flag={ENABLE_ITEM_MODIFIERS}
                        loader={<MoniebookLoader fullHeight />}>
                        <ProtectedView
                            accessCodeList={ITEMS_PERMISSIONS}
                            grantAccessIfAny
                        />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { modifiersList } = await unwrapFeatures();

                            return modifiersList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'create',
                        element: (
                            <ProtectedView accessCodeList={['CREATE_ITEM']} />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { createModifier } =
                                        await unwrapFeatures();

                                    return createModifier().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: ':id/edit',
                        element: (
                            <ProtectedView accessCodeList={['EDIT_ITEM']} />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { editModifier } =
                                        await unwrapFeatures();

                                    return editModifier().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'settings/measurement-units',
                element: (
                    <ProtectedView
                        accessCodeList={MANAGE_MEASUREMENT_UNITS_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { measurementUnitsList } =
                                await unwrapFeatures();
                            return measurementUnitsList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={[
                                    'CREATE_MEASUREMENT_UNIT',
                                    'MANAGE_MEASUREMENT_UNIT',
                                ]}
                                grantAccessIfAny
                            />
                        ),
                        children: [
                            {
                                path: 'create',
                                async lazy() {
                                    const { createUnit } =
                                        await unwrapFeatures();
                                    return createUnit().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={[
                                    'EDIT_MEASUREMENT_UNIT',
                                    'MANAGE_MEASUREMENT_UNIT',
                                ]}
                                grantAccessIfAny
                            />
                        ),
                        children: [
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { updateUnit } =
                                        await unwrapFeatures();
                                    return updateUnit().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'settings/pricing-groups',
                element: (
                    <ProtectedView
                        accessCodeList={PRICING_GROUPS_PERMISSIONS}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { pricingGroupsList } =
                                await unwrapFeatures();
                            return pricingGroupsList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={['CREATE_PRICING_GROUP']}
                            />
                        ),
                        children: [
                            {
                                path: 'create',
                                async lazy() {
                                    const { createPricingGroup } =
                                        await unwrapFeatures();
                                    return createPricingGroup().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={['UPDATE_PRICING_GROUP']}
                            />
                        ),
                        children: [
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { updatePricingGroup } =
                                        await unwrapFeatures();
                                    return updatePricingGroup().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'settings/procurement',
                element: (
                    <FeatureProtectedView
                        flag={ENABLE_PROCUREMENT_SETTINGS}
                        loader={<MoniebookLoader fullHeight />}>
                        <ProtectedView
                            accessCodeList={['APPROVE_PURCHASE_ORDERS']}
                        />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { procurementSettings } =
                                await unwrapFeatures();

                            return procurementSettings().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'stock-history',
                element: (
                    <ProtectedView
                        accessCodeList={STOCK_HISTORY_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { stockAdjustmentHistory } =
                                await unwrapFeatures();
                            return stockAdjustmentHistory().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'stock-adjustment',
                element: (
                    <ProtectedView
                        accessCodeList={STOCK_ADJUSTMENT_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: ':id?',
                        async lazy() {
                            const { stockAdjustmentList } =
                                await unwrapFeatures();
                            return stockAdjustmentList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'create',
                        element: (
                            <ProtectedView
                                accessCodeList={['MANAGE_STOCK_ADJUSTMENT']}
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { createStockAdjustment } =
                                        await unwrapFeatures();
                                    return createStockAdjustment().then(
                                        (m) => ({ Component: m.default }),
                                    );
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'categories',
                element: (
                    <ProtectedView
                        accessCodeList={ITEMS_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { categoriesList } = await unwrapFeatures();
                            return categoriesList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'create',
                        async lazy() {
                            const { createCategory } = await unwrapFeatures();
                            return createCategory().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':id/edit',
                        async lazy() {
                            const { updateCategory } = await unwrapFeatures();
                            return updateCategory().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'suppliers',
                element: (
                    <ProtectedView
                        accessCodeList={SUPPLIERS_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { suppliersList } = await unwrapFeatures();
                            return suppliersList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={['MANAGE_SUPPLIER']}
                            />
                        ),
                        children: [
                            {
                                path: 'create',
                                async lazy() {
                                    const { createSupplier } =
                                        await unwrapFeatures();
                                    return createSupplier().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={[
                                    'MANAGE_SUPPLIER',
                                    'EDIT_SUPPLIER',
                                ]}
                                grantAccessIfAny
                            />
                        ),
                        children: [
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { updateSupplier } =
                                        await unwrapFeatures();
                                    return updateSupplier().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'reports/inventory-valuation',
                element: (
                    <ProtectedView
                        accessCodeList={REPORTS_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { inventoryValuationList } =
                                await unwrapFeatures();
                            return inventoryValuationList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'reports/expiring-inventory',
                element: (
                    <ProtectedView
                        accessCodeList={REPORTS_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { expiringInventory } =
                                await unwrapFeatures();
                            return expiringInventory().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'reports/stock-movements',
                element: (
                    <ProtectedView
                        accessCodeList={REPORTS_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { stockMovement } = await unwrapFeatures();
                            return stockMovement().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'batch-management',
                element: (
                    <FeatureProtectedView
                        loader={<MoniebookLoader fullHeight />}>
                        <ProtectedView
                            accessCodeList={BATCH_MANAGEMENT_PERMISSIONS}
                        />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { inventoryBatchList } =
                                await unwrapFeatures();
                            return inventoryBatchList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':id/edit',
                        async lazy() {
                            const { updateInventoryBatch } =
                                await unwrapFeatures();
                            return updateInventoryBatch().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'stock-transfer',
                async lazy() {
                    const { stockTransferPaywallGuard } =
                        await unwrapFeatures();
                    return stockTransferPaywallGuard().then((m) => ({
                        Component: m.default,
                    }));
                },
                children: [
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={STOCK_TRANSFER_PERMISSIONS}
                                grantAccessIfAny
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { transferOrderList } =
                                        await unwrapFeatures();
                                    return transferOrderList().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={['CREATE_STOCK_TRANSFER']}
                            />
                        ),
                        children: [
                            {
                                path: 'create',
                                async lazy() {
                                    const { createTransferOrder } =
                                        await unwrapFeatures();
                                    return createTransferOrder().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={['RECEIVE_STOCK_TRANSFER']}
                            />
                        ),
                        children: [
                            {
                                path: ':id/receive',
                                async lazy() {
                                    const { receiveTransferOrder } =
                                        await unwrapFeatures();
                                    return receiveTransferOrder().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'production-recipes',
                async lazy() {
                    const { productionRecipesPaywallGuard } =
                        await unwrapFeatures();
                    return productionRecipesPaywallGuard().then((m) => ({
                        Component: m.default,
                    }));
                },
                children: [
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={
                                    PRODUCTION_AND_RECIPES_PERMISSIONS
                                }
                                grantAccessIfAny
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { recipesAndProduction } =
                                        await unwrapFeatures();
                                    return recipesAndProduction().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: 'create-recipe',
                        async lazy() {
                            const { createRecipe } = await unwrapFeatures();
                            return createRecipe().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':id/edit-recipe',
                        async lazy() {
                            const { editRecipe } = await unwrapFeatures();
                            return editRecipe().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'start-production',
                        async lazy() {
                            const { startProduction } = await unwrapFeatures();
                            return startProduction().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':id/edit-production',
                        async lazy() {
                            const { editProduction } = await unwrapFeatures();
                            return editProduction().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'purchase-orders',
                element: (
                    <ProtectedView
                        accessCodeList={PURCHASE_ORDERS_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { purchaseOrdersList } =
                                await unwrapFeatures();
                            return purchaseOrdersList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'method-selection',
                        async lazy() {
                            const { methodSelection } = await unwrapFeatures();
                            return methodSelection().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={[
                                    'CREATE_PURCHASE_ORDERS',
                                    'DRAFT_PURCHASE_ORDERS',
                                ]}
                                grantAccessIfAny
                            />
                        ),
                        children: [
                            {
                                path: 'create',
                                async lazy() {
                                    const { createPurchaseOrder } =
                                        await unwrapFeatures();
                                    return createPurchaseOrder().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { updatePurchaseOrder } =
                                        await unwrapFeatures();
                                    return updatePurchaseOrder().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: ':poId',
                        element: (
                            <FeatureProtectedView
                                flag={ENABLE_PRICE_REVIEW_AND_BILL_FLAG}
                                loader={<MoniebookLoader fullHeight />}>
                                <Outlet />
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { purchaseOrderDetails } =
                                        await unwrapFeatures();
                                    return purchaseOrderDetails().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: 'goods-received/:grnId',
                                element: (
                                    <ProtectedView
                                        accessCodeList={['VIEW_GRN']}
                                    />
                                ),
                                children: [
                                    {
                                        path: '',
                                        async lazy() {
                                            const { goodsReceivedDetails } =
                                                await unwrapFeatures();

                                            return goodsReceivedDetails().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                    {
                                        path: 'edit',
                                        element: (
                                            <ProtectedView
                                                accessCodeList={['CREATE_GRN']}
                                            />
                                        ),
                                        children: [
                                            {
                                                path: '',
                                                async lazy() {
                                                    const {
                                                        goodsReceivedEdit,
                                                    } = await unwrapFeatures();

                                                    return goodsReceivedEdit().then(
                                                        (m) => ({
                                                            Component:
                                                                m.default,
                                                        }),
                                                    );
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'bills/create',
                                element: (
                                    <ProtectedView
                                        accessCodeList={['CREATE_BILL']}
                                    />
                                ),
                                children: [
                                    {
                                        path: '',
                                        async lazy() {
                                            const { createBill } =
                                                await unwrapFeatures();

                                            return createBill().then((m) => ({
                                                Component: m.default,
                                            }));
                                        },
                                    },
                                ],
                            },
                            {
                                path: 'bills/:billId',
                                element: (
                                    <ProtectedView
                                        accessCodeList={['VIEW_BILL']}
                                    />
                                ),
                                children: [
                                    {
                                        path: '',
                                        async lazy() {
                                            const { billDetails } =
                                                await unwrapFeatures();

                                            return billDetails().then((m) => ({
                                                Component: m.default,
                                            }));
                                        },
                                    },
                                ],
                            },
                            {
                                path: 'pricing-reviews/:reviewId',
                                element: (
                                    <ProtectedView
                                        accessCodeList={['MANAGE_ITEM']}
                                    />
                                ),
                                children: [
                                    {
                                        path: '',
                                        async lazy() {
                                            const { pricingReviewDetails } =
                                                await unwrapFeatures();

                                            return pricingReviewDetails().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                    {
                                        path: 'review',
                                        async lazy() {
                                            const { reviewPricingReviews } =
                                                await unwrapFeatures();

                                            return reviewPricingReviews().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: ':id/receive',
                        async lazy() {
                            const { receivePurchaseOrder } =
                                await unwrapFeatures();
                            return receivePurchaseOrder().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':poId/receive-goods',
                        element: (
                            <FeatureProtectedView
                                flag={ENABLE_PRICE_REVIEW_AND_BILL_FLAG}
                                loader={<MoniebookLoader fullHeight />}>
                                <ProtectedView
                                    accessCodeList={['CREATE_GRN']}
                                />
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { receiveGoods } =
                                        await unwrapFeatures();

                                    return receiveGoods().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'purchase-order-bills',
                element: (
                    <FeatureProtectedView
                        flag={ENABLE_PRICE_REVIEW_AND_BILL_FLAG}
                        loader={<MoniebookLoader fullHeight />}>
                        <ProtectedView
                            accessCodeList={BILL_PERMISSIONS}
                            grantAccessIfAny
                        />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { billsList } = await unwrapFeatures();
                            return billsList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'create',
                        element: (
                            <ProtectedView accessCodeList={['CREATE_BILL']} />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { createBill } =
                                        await unwrapFeatures();

                                    return createBill().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: ':billId',
                        async lazy() {
                            const { billDetails } = await unwrapFeatures();
                            return billDetails().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'goods-received',
                element: (
                    <FeatureProtectedView
                        flag={ENABLE_PRICE_REVIEW_AND_BILL_FLAG}
                        loader={<MoniebookLoader fullHeight />}>
                        <ProtectedView
                            accessCodeList={GRN_PERMISSIONS}
                            grantAccessIfAny
                        />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { goodsReceivedList } =
                                await unwrapFeatures();

                            return goodsReceivedList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':grnId',
                        async lazy() {
                            const { goodsReceivedDetails } =
                                await unwrapFeatures();

                            return goodsReceivedDetails().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':grnId/edit',
                        element: (
                            <ProtectedView accessCodeList={['CREATE_GRN']} />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { goodsReceivedEdit } =
                                        await unwrapFeatures();

                                    return goodsReceivedEdit().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'pricing-reviews',
                element: (
                    <FeatureProtectedView
                        flag={ENABLE_PRICE_REVIEW_AND_BILL_FLAG}
                        loader={<MoniebookLoader fullHeight />}>
                        <ProtectedView
                            accessCodeList={['MANAGE_ITEM']}
                            grantAccessIfAny
                        />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { pricingReviewsList } =
                                await unwrapFeatures();
                            return pricingReviewsList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':reviewId',
                        async lazy() {
                            const { pricingReviewDetails } =
                                await unwrapFeatures();
                            return pricingReviewDetails().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':reviewId/review',
                        async lazy() {
                            const { reviewPricingReviews } =
                                await unwrapFeatures();
                            return reviewPricingReviews().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'stock-reconciliation',
                element: (
                    <ProtectedView
                        accessCodeList={STOCK_RECONCILIATION_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { stockReconciliationList } =
                                await unwrapFeatures();
                            return stockReconciliationList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={['CREATE_STOCK_RECONCILIATION']}
                                grantAccessIfAny
                            />
                        ),
                        children: [
                            {
                                path: 'create',
                                async lazy() {
                                    const { createStockReconciliation } =
                                        await unwrapFeatures();
                                    return createStockReconciliation().then(
                                        (m) => ({ Component: m.default }),
                                    );
                                },
                            },
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { editStockReconciliation } =
                                        await unwrapFeatures();
                                    return editStockReconciliation().then(
                                        (m) => ({ Component: m.default }),
                                    );
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'reports/reconciliation-report',
                element: (
                    <ProtectedView
                        accessCodeList={REPORTS_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { reconciliationReport } =
                                await unwrapFeatures();
                            return reconciliationReport().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'stock-overview',
                element: (
                    <ProtectedView
                        accessCodeList={STOCK_ADJUSTMENT_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { stockOverview } = await unwrapFeatures();
                            return stockOverview().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
        ],
    },
];

async function unwrapFeatures() {
    const features = await import('products/features');

    return features.default;
}
